import { TRYON_SERVER_URL } from "../config";

const API_PREFIXES = ["/api/v1/agent", "/agent"];
const DEFAULT_TIMEOUT_MS = 15000;

function getAuthToken() {
    return localStorage.getItem("accessToken");
}

function requireToken() {
    const token = getAuthToken();
    if (!token) throw new Error("Please sign in to use TryOn AI.");
    return token;
}

async function errorMessageFrom(response) {
    if (response.status === 401) return "Your session has expired. Please sign in again.";
    try {
        const data = await response.json();
        return data.detail || data.message || data.error || `Request failed (${response.status})`;
    } catch {
        return `Request failed (${response.status})`;
    }
}

async function request(path, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
    const token = requireToken();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const response = await fetch(`${TRYON_SERVER_URL}${path}`, {
            ...options,
            signal: controller.signal,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...options.headers,
            },
        });
        return response;
    } catch (err) {
        if (err.name === "AbortError") throw new Error("The request timed out. Please try again.");
        throw err;
    } finally {
        clearTimeout(timer);
    }
}

/** Tries the versioned route first, then the legacy one if the server answers 404. */
async function call(suffix, options) {
    let response;
    for (const prefix of API_PREFIXES) {
        response = await request(`${prefix}${suffix}`, options);
        if (response.status !== 404) break;
    }
    if (!response.ok) throw new Error(await errorMessageFrom(response));
    if (response.status === 204 || !response.headers.get("content-type")?.includes("application/json")) {
        return undefined;
    }
    return response.json();
}

export function listSessions(page = 1, pageSize = 50) {
    return call(`/sessions/?page=${page}&page_size=${pageSize}`);
}

export function createSession(title) {
    return call("/sessions/", { method: "POST", body: JSON.stringify(title ? { title } : {}) });
}

export function getSession(sessionId) {
    return call(`/sessions/${sessionId}/`);
}

export function deleteSession(sessionId) {
    return call(`/sessions/${sessionId}/`, { method: "DELETE" });
}

export async function listSessionAssets(sessionId) {
    try {
        return await call(`/sessions/${sessionId}/assets/`);
    } catch {
        return { count: 0, assets: [] };
    }
}

/** Fetches an auth-protected asset and returns a blob: URL the browser can render. */
export async function fetchAssetBlobUrl(url) {
    const token = requireToken();
    const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) throw new Error(`Failed to fetch asset (${response.status})`);
    return URL.createObjectURL(await response.blob());
}

// ── SSE streaming ───────────────────────────────────────────────────────────

async function parseSSEStream(reader, onEvent) {
    const decoder = new TextDecoder("utf-8", { fatal: false });
    let buffer = "";
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let boundary;
        while ((boundary = buffer.indexOf("\n\n")) !== -1) {
            const block = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            let id = null;
            let data = "";
            for (const line of block.split("\n")) {
                if (line.startsWith("id:")) id = line.slice(3).trim();
                else if (line.startsWith("data:")) data += (data ? "\n" : "") + line.slice(5).trim();
            }
            if (data) onEvent(id, data);
        }
    }
}

function errorEvent(code, message) {
    return { type: "error", code, message };
}

const PRE_STREAM_ERRORS = {
    401: ["auth_error", "Your session has expired. Please sign in again."],
    402: ["payment_required", "You're out of credits. Top up your account to continue."],
    409: ["generation_in_progress", "A generation is already in progress. Please wait."],
};

class PermanentError extends Error {}

/**
 * Sends a message and streams back SSE events. `onEvent` receives parsed
 * events; transport failures arrive as synthetic `{type: "error"}` events.
 * Returns a function that aborts the stream.
 */
export function streamMessage({ sessionId, content, images, onEvent, signal }) {
    const controller = new AbortController();
    const abortSignal = signal ?? controller.signal;
    let lastEventId = null;

    const attempt = async () => {
        const token = requireToken();
        const headers = { Authorization: `Bearer ${token}`, Accept: "text/event-stream" };
        let body;
        if (images?.length) {
            body = new FormData();
            if (content) body.append("content", content);
            images.forEach((file) => body.append("images", file));
        } else {
            body = JSON.stringify({ content });
            headers["Content-Type"] = "application/json";
        }
        if (lastEventId) headers["Last-Event-ID"] = lastEventId;

        const response = await fetch(`${TRYON_SERVER_URL}/api/v1/agent/sessions/${sessionId}/messages/`, {
            method: "POST",
            signal: abortSignal,
            headers,
            body,
        });

        if (!response.ok) {
            const known = PRE_STREAM_ERRORS[response.status];
            if (known) onEvent(errorEvent(...known));
            else if (response.status >= 400 && response.status < 500) {
                const body = await response.json().catch(() => null);
                onEvent(errorEvent("HTTP_CLIENT_ERROR", body?.detail || "Request failed. Please try again."));
            } else {
                throw new Error(`HTTP ${response.status}`);
            }
            throw new PermanentError();
        }
        if (!response.body) throw new Error("No response body from server");

        await parseSSEStream(response.body.getReader(), (id, data) => {
            if (id) lastEventId = id;
            try {
                onEvent(JSON.parse(data));
            } catch {
                // skip malformed frames
            }
        });
    };

    const backoff = [1000, 2000, 4000];
    (async () => {
        for (let i = 0; i <= backoff.length; i += 1) {
            try {
                await attempt();
                return;
            } catch (err) {
                if (err instanceof PermanentError || abortSignal.aborted || err.name === "AbortError") return;
                if (i === backoff.length) {
                    onEvent(errorEvent("NETWORK_ERROR", "Connection lost. Please try again."));
                    return;
                }
                await new Promise((resolve) => setTimeout(resolve, backoff[i]));
            }
        }
    })();

    return () => controller.abort();
}
